import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { errorMessage, useTRPC } from './trpc'
import { noteOwnActivity } from './portfolio'
import type { Custody } from './pool'

// The exchanges' marks ride inside the bundle as data URLs (John, Nov 4 2026: "CEX logos like coinbase tend to disappear
// occasionally"): every chip and menu row used to fetch its logo again from the API, and one slow or failed fetch left a
// blank until the next reload. A data URL cannot fail to load.
import binanceMark from '../assets/custody/binance.svg?inline'
import coinbaseMark from '../assets/custody/coinbase.svg?inline'
import krakenMark from '../assets/custody/kraken.svg?inline'
import kucoinMark from '../assets/custody/kucoin.png?inline'
import mexcMark from '../assets/custody/mexc.svg?inline'

/** Kraken's mark, for the withdraw dialog's Kraken button and chip (John, Oct 31 2026) */
export const KRAKEN_LOGO = krakenMark

/** the exchanges' and Sonar's marks (John, Oct 31 2026; Coinbase's is the wordmark's c on its blue, from the kit John supplied Nov 1 2026) */
export const CUSTODY_LOGOS: Record<Custody, string> = {
  sonar: '/brand/custody/sonar.png',
  binanceus: binanceMark,
  mexc: mexcMark,
  coinbase: coinbaseMark,
  /** KuCoin's symbol, the teal K on nothing, from the brand kit John supplied (Nov 4 2026) */
  kucoin: kucoinMark,
  /** Kraken as an exchange custody too (Nov 4 2026) */
  kraken: krakenMark,
}

/**
 * Switches where the account's money lives (John, Oct 30 2026: a toggle per
 * account). The header menu and Settings share it: the choice shows the
 * moment it is clicked, and everything read under the other custody is
 * dropped so its deposit address and lists never show under this one.
 */
export function useSetCustody() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const configKey = trpc.vault.config.queryKey()
  const set = useMutation(
    trpc.auth.setCustody.mutationOptions({
      onMutate: async (input) => {
        setError(null)
        await queryClient.cancelQueries({ queryKey: configKey })
        const previous = queryClient.getQueryData(configKey)
        if (previous) queryClient.setQueryData(configKey, { ...previous, custody: input.custody, exchange: true, pool: true })
        // the balances about to load belong to the other custody: the watcher must not announce them as deposits
        noteOwnActivity()
        return { previous }
      },
      onError: (err, _input, context) => {
        if (context?.previous) queryClient.setQueryData(configKey, context.previous)
        setError(errorMessage(err))
      },
      onSuccess: (user) => {
        queryClient.setQueryData(trpc.auth.me.queryKey(), user)
        // a pick is a small thing (John, Nov 1 2026: "i dont need strategies to keep reloading on new deposit or withdraw
        // destination click"): only what reads the remembered platform is refreshed, in the background, with its old data
        // kept on screen; the lists show every platform at all times, so they are left alone
        for (const key of [trpc.vault.config.queryKey(), trpc.vault.payoutFee.queryKey(), trpc.vault.holdings.queryKey()]) {
          void queryClient.invalidateQueries({ queryKey: key })
        }
      },
    }),
  )
  return { set, error }
}
